import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Img } from "@/components/ui/img";
import { featuredTherapistsQuery } from "@/api-queries/therapists";
import { defineSection } from "@/ssr/define-section";

// Served at a stable URL by the /assets/:file route (not bundler imports) so the
// src is identical in the client bundle and server-rendered HTML.
const THERAPISTS_IMAGE = "/assets/theapists-image.webp";

// Two mirrored organic "blob" shapes. All cells share one square footprint so
// the grid stays even; the mirrored radii give each photo a soft, hand-cut edge
// without breaking alignment. Slots alternate A / B / B / A for balance.
const BLOB_SHAPE_A = "46% 54% 57% 43% / 52% 46% 54% 48%";
const BLOB_SHAPE_B = "54% 46% 43% 57% / 46% 52% 48% 54%";
const BLOB_SHAPES = [BLOB_SHAPE_A, BLOB_SHAPE_B, BLOB_SHAPE_B, BLOB_SHAPE_A];

function TrustedTherapistsView() {
  const ref = useScrollReveal<HTMLElement>();
  const { t } = useTranslation();

  const { data: therapists } = useQuery(featuredTherapistsQuery);
  const featured = therapists?.slice(0, 4) ?? [];

  return (
    <section
      ref={ref}
      className="animate-on-scroll relative overflow-hidden py-16 sm:py-24"
      style={{ background: "oklch(98.5% 0.008 84)" }}
    >
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        {/* Copy */}
        <div className="order-2 text-center lg:order-1 lg:text-left">
          <h2
            className="font-heading font-black leading-[1.05] tracking-[-0.02em] text-foreground"
            style={{ fontSize: "clamp(1.9rem, 4vw, 3rem)" }}
          >
            {t("home.trustedTherapists.title")}
          </h2>
          <p className="mx-auto mt-5 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0">
            {t("home.trustedTherapists.description")}
          </p>
          <Link
            to="/therapists"
            className="group mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-all duration-300 hover:scale-[1.02]"
          >
            {t("home.trustedTherapists.cta")}
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden />
          </Link>
        </div>

        {/* Featured therapists, curated in Admin → Content. Falls back to the
            static group image when none are featured, so it is never empty. */}
        <div className="relative order-1 mx-auto w-full max-w-[480px] lg:order-2">
          {/* Soft brand glow behind the cluster for depth */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-[8%] rounded-full opacity-60 blur-3xl"
            style={{ background: "radial-gradient(circle, oklch(74.72% 0.089 82.39 / 0.20), transparent 70%)" }}
          />

          {featured.length === 0 ? (
            <img
              src={THERAPISTS_IMAGE}
              alt={t("home.trustedTherapists.title")}
              className="animate-hero-rise relative size-full"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="animate-hero-rise relative grid grid-cols-2 gap-x-6 gap-y-8 sm:gap-x-8">
              {featured.map((th, i) => {
                const photo = th.profileImageUrl || th.userImage;
                const initial = th.userName.charAt(0).toUpperCase();

                return (
                  <Link
                    key={th.id}
                    to="/therapists/$therapistId"
                    params={{ therapistId: th.slug ?? th.id }}
                    className="group flex flex-col items-center text-center"
                  >
                    {/* Photo — the only clipped element, so the name below is never cut */}
                    <div
                      className="relative aspect-square w-full overflow-hidden shadow-[0_14px_36px_oklch(20%_0.02_84_/_0.16)] transition-transform duration-500 ease-out group-hover:-translate-y-1.5 group-hover:scale-[1.03]"
                      style={{ borderRadius: BLOB_SHAPES[i] ?? "50%" }}
                    >
                      {photo ? (
                        <Img
                          src={photo}
                          alt={th.userName}
                          sizes="(min-width: 1024px) 220px, 40vw"
                          className="size-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                        />
                      ) : (
                        <div
                          className="flex size-full items-center justify-center"
                          style={{ background: "linear-gradient(135deg, oklch(85% 0.06 84), oklch(78% 0.1 84))" }}
                        >
                          <span
                            className="text-5xl font-bold"
                            style={{ fontFamily: "var(--font-heading)", color: "oklch(50% 0.12 84)" }}
                          >
                            {initial}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Name below the photo */}
                    <h3
                      className="mt-4 text-[15px] italic leading-tight tracking-[-0.01em] text-foreground transition-colors duration-300 group-hover:[color:oklch(37.12%_0.059_186.62)] sm:text-base"
                      style={{ fontFamily: "var(--font-heading)", fontWeight: 500 }}
                    >
                      {th.userName}
                    </h3>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export const TrustedTherapists = defineSection({
  Component: TrustedTherapistsView,
  queries: [featuredTherapistsQuery],
});
